import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "components/ui/AppLayout";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import WhatsAppConnection from "components/whatsapp/WhatsAppConnection";
import { orgApi } from "api/org";
import { playbooksApi } from "api/playbooks";
import { importsApi } from "api/imports";

function StepBadge({ done }) {
  return (
    <span
      className="flex items-center justify-center rounded-full flex-shrink-0"
      style={{
        width: 28,
        height: 28,
        background: done ? "#DCFCE7" : "var(--color-muted)",
        color: done ? "#15803D" : "var(--color-text-secondary)",
      }}
    >
      <Icon name={done ? "Check" : "Circle"} size={15} color="currentColor" />
    </span>
  );
}

/**
 * Onboarding wizard (Sprint 9, Part 89 §89.5) — the 10-minute pilot path:
 * 1. Workspace (company + vertical playbook preset)
 * 2. WhatsApp phone (support channel)
 * 3. Import leads (CSV)
 * 4. Today — the north-star event (first follow-up sent)
 * Every step writes through the real API; the gate redirects here until done.
 */
export default function Onboarding() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [org, setOrg] = useState(null);
  const [playbooks, setPlaybooks] = useState([]);
  const [selectedVertical, setSelectedVertical] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    const [onboarding, orgData, playbookItems] = await Promise.all([
      orgApi.getOnboarding(),
      orgApi.getOrg(),
      playbooksApi.list(),
    ]);
    setStatus(onboarding);
    setOrg(orgData);
    setPlaybooks(playbookItems);
    setCompanyName(orgData?.name || "");
    setSelectedVertical(orgData?.vertical || "");
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e?.message || "Unable to load onboarding status."))
      .finally(() => setIsLoading(false));
  }, []);

  const steps = useMemo(() => {
    const s = status?.steps || {};
    return [
      { id: "workspace", label: "Set up your workspace", done: s.signup, detail: "Company + vertical playbook" },
      { id: "whatsapp", label: "Connect WhatsApp", done: s.whatsappConnected, detail: "The phone customers message" },
      { id: "import", label: "Import your leads", done: s.hasData, detail: "CSV batch import" },
      { id: "followup", label: "First AI follow-up", done: s.firstFollowUpSent, detail: "The north-star event — approve & send from Today" },
    ];
  }, [status]);

  const saveWorkspace = async () => {
    setIsSaving(true);
    setError("");
    try {
      const patch = {};
      if (companyName.trim() && companyName.trim() !== org?.name) patch.name = companyName.trim();
      if (selectedVertical) patch.vertical = selectedVertical;
      await orgApi.updateOrg(patch);
      await load();
    } catch (e) {
      setError(e?.message || "Could not save workspace settings.");
    } finally {
      setIsSaving(false);
    }
  };

  // Re-audit fix — escape hatch: a user without a CSV or real WhatsApp number
  // must never be trapped on the wizard. Skip opens the dashboard.
  const handleSkip = async () => {
    setError("");
    try {
      await orgApi.updateOrg({ onboardingSkipped: true });
      navigate("/today");
    } catch (e) {
      setError(e?.message || "Could not skip setup — try again.");
    }
  };

  const uploadCsv = async (file) => {
    if (!file) return;
    setIsUploading(true);
    setError("");
    try {
      const batch = await importsApi.upload(file);
      for (let i = 0; i < 20; i += 1) {
        const current = await importsApi.get(batch.id);
        if (current?.status === "done" || current?.status === "partial" || current?.status === "failed") break;
        await new Promise((r) => setTimeout(r, 500));
      }
      await load();
    } catch (e) {
      setError(e?.message || "Import failed.");
    } finally {
      setIsUploading(false);
    }
  };

  const doneCount = steps.filter((s) => s.done).length;

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
        <main className="px-4 md:px-6 lg:px-8 py-6 max-w-3xl mx-auto">
          <div className="mb-6">
            <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)" }}>
              Set up your CRM
            </h1>
            <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
              About 10 minutes to your first AI follow-up. {doneCount}/4 steps done.
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border px-4 py-3 flex items-center justify-between gap-4" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
              <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{error}</p>
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                iconPosition="left"
                iconSize={14}
                onClick={() => {
                  setError("");
                  setIsLoading(true);
                  load()
                    .catch((e) => setError(e?.message || "Unable to load onboarding status."))
                    .finally(() => setIsLoading(false));
                }}
              >
                Retry
              </Button>
            </div>
          )}

          {isLoading ? (
            <div className="rounded-xl border px-4 py-16 text-center" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
              <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Checking your workspace setup...</p>
            </div>
          ) : (
          <>

          {/* Step 1 — workspace */}
          <section className="rounded-xl border mb-4 p-5" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
            <div className="flex items-center gap-3 mb-4">
              <StepBadge done={steps[0]?.done} />
              <div>
                <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>
                  Your workspace
                </h2>
                <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
                  Company name + the playbook preset that matches your vertical
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {playbooks.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedVertical(p.id)}
                  className="text-left rounded-lg border p-4 transition-all duration-150"
                  style={{
                    background: selectedVertical === p.id ? "var(--color-muted)" : "var(--color-card)",
                    borderColor: selectedVertical === p.id ? "var(--color-primary)" : "var(--color-border)",
                  }}
                >
                  <p className="font-caption text-sm font-semibold" style={{ color: "var(--color-foreground)" }}>
                    {p.label}
                  </p>
                  <p className="font-caption text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>
                    {p.description}
                  </p>
                  <p className="font-caption text-xs mt-2" style={{ color: "var(--color-primary)" }}>
                    {p.followUpDrafts.length} follow-up drafts ready
                  </p>
                </button>
              ))}
            </div>
            <input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Company name"
              className="w-full mb-3 px-3 py-2 rounded-md border font-caption text-sm"
              style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)" }}
              aria-label="Company name"
            />
            <Button variant="outline" size="sm" onClick={saveWorkspace} disabled={isSaving || !selectedVertical}>
              {steps[0]?.done ? "Update" : "Save workspace"}
            </Button>
          </section>

          {/* Step 2 — whatsapp */}
          <section className="rounded-xl border mb-4 p-5" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
            <div className="flex items-center gap-3 mb-4">
              <StepBadge done={steps[1]?.done} />
              <div>
                <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>
                  WhatsApp support channel
                </h2>
                <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
                  The phone number customers message on WhatsApp. We reply within Indian business hours (9am–9pm IST) and
                  auto-reply outside them.
                </p>
              </div>
            </div>
            <WhatsAppConnection onSaved={load} />
          </section>

          {/* Step 3 — import */}
          <section className="rounded-xl border mb-4 p-5" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
            <div className="flex items-center gap-3 mb-4">
              <StepBadge done={steps[2]?.done} />
              <div>
                <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>
                  Import your leads
                </h2>
                <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
                  Upload a CSV with columns name, email, phone, company, value. Duplicates are skipped, and you can undo
                  the batch later.
                </p>
              </div>
            </div>
            <label
              className="flex items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-6 cursor-pointer font-caption text-sm"
              style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}
            >
              <Icon name="Upload" size={16} color="currentColor" />
              {isUploading ? "Importing..." : "Choose a CSV to import"}
              <input type="file" accept=".csv" className="hidden" onChange={(e) => uploadCsv(e.target.files?.[0])} />
            </label>
            {steps[2]?.done && (
              <p className="font-caption text-xs mt-2" style={{ color: "var(--color-success)" }}>
                Leads are in — you can import more or undo batches from the Leads page.
              </p>
            )}
            {!steps[2]?.done && (
              <p className="font-caption text-xs mt-2" style={{ color: "var(--color-text-secondary)" }}>
                No CSV handy?{" "}
                <Link to="/leads" className="hover:underline" style={{ color: "var(--color-primary)" }}>
                  Add leads manually on the Leads page
                </Link>{" "}
                — any lead counts as data.
              </p>
            )}
          </section>

          {/* Step 4 — north star */}
          <section className="rounded-xl border mb-6 p-5" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
            <div className="flex items-center gap-3 mb-4">
              <StepBadge done={steps[3]?.done} />
              <div>
                <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>
                  Your first AI follow-up
                </h2>
                <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
                  The north-star event: an untouched lead gets a drafted follow-up, you approve it from Today, and it
                  goes out on WhatsApp.
                </p>
              </div>
            </div>
            {steps[3]?.done ? (
              <p className="font-caption text-sm mb-3" style={{ color: "var(--color-success)" }}>
                Done — your first AI follow-up has been sent. Welcome to the pilot!
              </p>
            ) : (
              <p className="font-caption text-sm mb-3" style={{ color: "var(--color-text-secondary)" }}>
                Follow-ups appear in Today once a lead has been untouched for 24h (or a deal idle for 48h).
              </p>
            )}
            <Button variant="default" size="sm" iconName="ArrowRight" iconPosition="right" onClick={() => navigate("/today")}>
              {steps[3]?.done ? "Go to Today" : "See Today"}
            </Button>
            {!steps[3]?.done && (
              <span className="ml-3 font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
                <Link to="/inbox" className="hover:underline" style={{ color: "var(--color-primary)" }}>
                  or answer messages in the inbox
                </Link>
              </span>
            )}
          </section>

          {/* Re-audit fix — never trap the user: escape hatch for users without
              a CSV file or WhatsApp number. */}
          <div className="text-center mb-6">
            <button
              onClick={handleSkip}
              className="font-caption text-sm hover:underline"
              style={{ color: "var(--color-text-secondary)", cursor: "pointer" }}
            >
              Skip setup for now — explore the dashboard
            </button>
          </div>
          </>
          )}
        </main>
      </div>
    </AppLayout>
  );
}
